/**
 * Shown when a fetch fails with `NetworkError` or `ServerError`.
 *
 * Visually distinct from `EmptyState` (which means "0 results") so the
 * staff member doesn't mistake a backend outage for a legitimate empty
 * response. Ships with its own icon + CTA; callers just hand it an
 * `onRetry` and optional `variant`.
 */

import type { ReactNode } from 'react';
import { motion, type Variants } from 'framer-motion';
import { cn } from '@/utils/cn';
import { spring } from '@/lib/motion';
import { Button } from './Button';

export type ConnectionErrorVariant = 'network' | 'server';

interface ConnectionErrorStateProps {
  /** `network` → couldn't reach the backend at all (offline, DNS, timeout).
   *  `server` → backend responded 5xx. Defaults to `network`. */
  variant?: ConnectionErrorVariant;
  title?: string;
  description?: string;
  onRetry: () => void;
  secondaryAction?: ReactNode;
  className?: string;
}

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: spring.gentle },
};

const iconBadge: Variants = {
  hidden: { opacity: 0, scale: 0.6, rotate: -8 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: spring.bouncy,
  },
};

const NetworkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 1l22 22" />
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
    <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

const ServerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const COPY: Record<
  ConnectionErrorVariant,
  { title: string; description: string }
> = {
  network: {
    title: 'Sin conexión',
    description:
      'No pudimos conectarnos con los servidores. Revisa tu conexión a internet e intenta de nuevo.',
  },
  server: {
    title: 'Servidor no disponible',
    description:
      'Los servidores están experimentando problemas. Intenta de nuevo en unos minutos.',
  },
};

export function ConnectionErrorState({
  variant = 'network',
  title,
  description,
  onRetry,
  secondaryAction,
  className,
}: ConnectionErrorStateProps) {
  const copy = COPY[variant];
  const Icon = variant === 'server' ? ServerIcon : NetworkIcon;

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center py-12 text-center',
        className,
      )}
    >
      <motion.div
        variants={iconBadge}
        className="relative mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-error-subtle text-error"
      >
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full border border-error/20"
        />
        <span className="relative">
          <Icon />
        </span>
      </motion.div>
      <motion.h3
        variants={item}
        className="font-display text-lg tracking-tight text-foreground"
      >
        {title ?? copy.title}
      </motion.h3>
      <motion.p
        variants={item}
        className="mt-1 max-w-sm text-sm text-muted-foreground"
      >
        {description ?? copy.description}
      </motion.p>
      <motion.div variants={item} className="mt-5 flex items-center gap-2">
        <Button onClick={onRetry}>Reintentar</Button>
        {secondaryAction}
      </motion.div>
    </motion.div>
  );
}
